import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuthContext } from 'domain/auth/hooks/useAuthContext';
import { useTranslationContext } from 'hooks/useTranslationContext';
import { en } from 'locales/config';
import { HostContext, HostUser, Language } from 'mfe/types';
import { getAccessToken as getFreshAccessToken } from 'utils/jwt';

// Le jeton d'accès ne vit que 5 minutes et le serveur ne le vérifie qu'à la réception complète de
// chaque requête. Les remotes téléversent des pièces jointes qui, sur une connexion lente, peuvent
// durer plusieurs minutes : un jeton remis avec 30 s de vie expirait en route et la transaction
// restait en brouillon. On garantit donc 4 minutes de validité à chaque jeton remis à un remote,
// au prix d'un renouvellement plus fréquent.
const REMOTE_TOKEN_MIN_REMAINING_SECONDS = 240;

// Compose le HostContext passe aux remotes. Doit etre appele sous
// BrowserRouter (useNavigate).
function useHostContextValue(): HostContext {
  const { userRole } = useAuthContext();
  const { language, switchLanguage } = useTranslationContext();
  const navigate = useNavigate();

  const currentLanguage: Language = language === en ? 'en' : 'fr';

  return useMemo(
    () => ({
      // L'enum UserRole du host a exactement les valeurs du type litteral du contrat.
      user: { role: userRole as HostUser['role'] },
      // Même chemin que les appels API du host (utils/jwt) : token rafraîchi
      // à la demande, jamais le state figé du login — un remote monté depuis
      // plus longtemps que la durée de vie du token recevrait un 401 sinon.
      getAccessToken: async () =>
        (await getFreshAccessToken({ minRemainingSeconds: REMOTE_TOKEN_MIN_REMAINING_SECONDS })) ?? null,
      navigateTo: (path: string) => navigate(path),
      language: currentLanguage,
      // switchLanguage du host est un toggle fr<->en : on ne bascule que si
      // la langue demandee differe.
      setLanguage: async (lang: Language) => {
        if (lang !== currentLanguage) await switchLanguage();
      },
    }),
    [userRole, currentLanguage, switchLanguage, navigate],
  );
}

export { useHostContextValue };
